/**
 * Agentic inbox contracts: autopilot policy, the escalation ladder, per
 * conversation agent state, AI drafts, the activity timeline, inbox stats and
 * the knowledge base.
 *
 * Most of these have no API yet. The web app binds to them through
 * `apps/web/src/shared/services/inbox-agent.service.ts`, which marks every
 * missing endpoint with `TODO(api)` and serves a mock adapter meanwhile. The
 * full list lives in docs/UI_REVAMP_API_GAPS.md.
 */

/** Uppercase platform enum name as stored by the API, e.g. `INSTAGRAM`. */
export type InboxChannel = string;

/** What the agent may do on a channel. */
export type AutopilotMode = 'off' | 'suggest' | 'auto';

export const AUTOPILOT_MODES: readonly AutopilotMode[] = ['off', 'suggest', 'auto'];

/**
 * Intent labels the classifier (A02) produces. The money-and-risk ones double
 * as escalation reasons so a blocked intent and its reason read the same.
 */
export const INBOX_INTENTS = [
  'opening_hours',
  'pricing',
  'booking',
  'delivery',
  'product_question',
  'general_question',
  'refund_or_payment',
  'complaint',
  'legal_or_safety',
  'other',
] as const;

export type InboxIntent = (typeof INBOX_INTENTS)[number];

/** Blueprint rule: refunds, legal, complaints never auto-send. */
export const DEFAULT_BLOCKED_INTENTS: readonly InboxIntent[] = ['refund_or_payment', 'legal_or_safety', 'complaint'];

/** Per-workspace agent policy: a default plus per-channel overrides. */
export interface AgentPolicy {
  defaultMode: AutopilotMode;
  perChannel: Partial<Record<InboxChannel, AutopilotMode>>;
  /** Auto mode only sends when the draft's confidence is at or above this (0–1). */
  autoSendThreshold: number;
  /**
   * Intents that never auto-send (A01 per-intent block list): the agent
   * escalates instead. Missing means DEFAULT_BLOCKED_INTENTS. Enforced
   * server-side once the policy endpoint exists.
   */
  blockedIntents?: string[];
  /**
   * When each channel was last switched to `auto`, ISO 8601, written by the
   * server. The agent never auto-answers a message older than this: turning
   * autopilot on must not reply to a backlog imported by the first sync.
   */
  channelEnabledAt?: Partial<Record<InboxChannel, string>>;
  /** Recorded owner consents that some channels need before auto-send works. */
  consents?: AgentConsents;
  /** How automated replies say they are automated. Cannot be switched off for auto-send. */
  disclosure?: AgentDisclosureSettings;
  /**
   * Whether customer messages may be sent to the AI provider (Anthropic,
   * processed in the US) at all: drafts, automatic replies and the weekly
   * question topics. False stops every one of them for the brand. Missing
   * means on wherever the agent is not switched off (`aiProcessingAllowed`),
   * so brands already using the agent keep working.
   */
  aiProcessing?: boolean;
  /** What the customer is told when the agent hands a conversation to a person. */
  handover?: AgentHandoverSettings;
  /**
   * Read-only, filled by the server from the per-platform policy table in
   * `@unipostudio/inbox-agent`, so the settings screen shows what each
   * channel allows without keeping a copy of the rules.
   */
  channelRules?: Partial<Record<InboxChannel, AgentChannelRule>>;
  /**
   * Read-only, from the server: channels whose account (a Page, an Instagram
   * account…) another brand connected first. That brand answers it by itself
   * and keeps its response clock; here the agent only suggests.
   * `handledIn` is that brand's name when it is in the same account.
   */
  sharedChannels?: Partial<Record<InboxChannel, { handledIn: string | null }>>;
}

/**
 * A policy as the browser saves it. A consent is given with `true` and
 * withdrawn with `false`/`null`; the server records who and when, and a
 * recorded consent sent back unchanged is kept as stored.
 */
export type AgentPolicyUpdate = Omit<AgentPolicy, 'consents'> & {
  consents?: Partial<Record<AgentConsentKey, AgentConsent | boolean | null>>;
};

/** Consents a channel can require before the agent may send there by itself. */
export type AgentConsentKey = 'googleReviewsAutoReply' | 'twitterAutomatedLabel';

export const AGENT_CONSENT_KEYS: readonly AgentConsentKey[] = ['googleReviewsAutoReply', 'twitterAutomatedLabel'];

/** Who agreed, and when. */
export interface AgentConsent {
  userId: string;
  at: string;
}

/**
 * How long a confirmation holds before it is asked again (IA-30): the X
 * "Automated" label can be switched off on X at any time, so the owner
 * confirms it again every 90 days. A key not listed holds until withdrawn.
 */
export const AGENT_CONSENT_MAX_AGE_DAYS: Readonly<Partial<Record<AgentConsentKey, number>>> = { twitterAutomatedLabel: 90 };

/** Whether a recorded confirmation still holds at `now`. */
export function agentConsentCurrent(
  key: AgentConsentKey,
  consent: AgentConsent | boolean | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!consent) return false;
  const days = AGENT_CONSENT_MAX_AGE_DAYS[key];
  if (days === undefined) return true;
  if (consent === true) return false;
  const at = Date.parse(consent.at);
  return Number.isFinite(at) && now.getTime() - at <= days * 86_400_000;
}

export type AgentConsents = Partial<Record<AgentConsentKey, AgentConsent | null>>;

/**
 * The disclosure an automated reply carries (EU AI Act Art. 50(1), Meta's
 * bot-disclosure policy, X's automated-account rules). Empty text means the
 * localised default; there is no way to turn it off for auto-send.
 */
export interface AgentDisclosureSettings {
  /** Line added to the first agent reply in a private conversation. Max 120 chars. */
  privateText?: string;
  /** Marker appended to every agent reply in public (comments, reviews). Max 60 chars. */
  publicText?: string;
  /** Also add the disclosure to drafts a person sends. Off by default: those are the person's. */
  onHumanSentDrafts?: boolean;
}

export interface AgentHandoverSettings {
  /** Tell the customer in a private conversation that a person will reply. Default on. */
  notifyPrivate?: boolean;
  /** Also say so under a public comment. Default off. */
  notifyPublic?: boolean;
  /** Add the brand's phone, email or hours to that message. Default on. */
  includeContact?: boolean;
}

/** What a channel allows the agent to do, as the settings screen shows it. */
export interface AgentChannelRule {
  /** Whether the agent may write replies here at all (drafts included). */
  agent: boolean;
  /** Whether auto-send can be switched on. */
  autoSend: boolean;
  /** Consent the owner must give before auto-send takes effect. */
  consent: AgentConsentKey | null;
  /** i18n key suffix under `inbox.autopilot.rule.` explaining a limit, or null. */
  note: string | null;
}

/** The fixed first rung of every ladder. It can be switched off, never removed. */
export const AI_LEVEL_ID = 'ai';

/**
 * `sentBy` of the agent's hand-over acknowledgement ("a person will reply
 * here soon"). Written by the agent, so it is automated, not a person.
 */
export const AGENT_HANDOVER_SENDER = 'ai-handover';

/**
 * Whether a reply's `sentBy` is the agent rather than a person. A person's
 * reply carries their user id (a UUID); everything the agent sends carries
 * `ai`, `ai-handover` or another `ai-` sender. Anything automated must never
 * be treated as a person's reply (Meta's HUMAN_AGENT tag is for people only).
 */
export function isAgentSender(sentBy: string | null | undefined): boolean {
  if (!sentBy) return true;
  return sentBy === AI_LEVEL_ID || sentBy === AGENT_HANDOVER_SENDER || sentBy.startsWith(`${AI_LEVEL_ID}-`);
}

export interface EscalationSla {
  /** Minutes allowed before the first human reply. */
  firstResponseMinutes: number;
  /** Minutes allowed before the conversation is resolved. */
  resolutionMinutes: number;
}

export interface EscalationNotify {
  inApp: boolean;
  email: boolean;
  whatsapp: boolean;
}

/** One human rung: Level 1, Level 2, … Order is the array order in the ladder. */
export interface EscalationLevel {
  id: string;
  name: string;
  /** Workspace member user ids who receive conversations at this level. */
  memberIds: string[];
  /** Channels this level covers. Empty means all channels. */
  channels: InboxChannel[];
  sla: EscalationSla;
  /** Move the conversation up automatically when the first-response SLA is breached. */
  autoEscalateOnBreach: boolean;
  notify: EscalationNotify;
}

/**
 * The agent is always the bottom rung and is not stored here. `levels` holds
 * the human levels from lowest to highest. The last one is the top of the
 * business (the owner or CEO): it always exists and cannot be removed. Any
 * number of levels may sit below it; a new workspace has only the top level.
 */
export interface EscalationLadder {
  levels: EscalationLevel[];
}

/**
 * What a workspace gets before anyone opens the agent settings.
 *
 * These live here rather than beside either caller because the server returns
 * them for a workspace that has never saved a policy, and the browser shows
 * them in the same screen: if the two disagreed, the settings page would show
 * one thing and the agent would do another.
 *
 * `suggest` is the default deliberately. A new workspace has an empty
 * knowledge base, so an agent set to `auto` would answer real customers out of
 * nothing; the owner turns that on once they can see what it drafts.
 */
/**
 * Whether the brand lets customer messages go to the AI provider. An
 * explicit choice wins; without one it is on unless the agent is off on
 * every channel (no stored policy is the default, Suggest: on).
 */
export function aiProcessingAllowed(policy: Partial<AgentPolicy> | null | undefined): boolean {
  if (typeof policy?.aiProcessing === 'boolean') return policy.aiProcessing;
  if (!policy) return true;
  const modes = Object.values(policy.perChannel ?? {});
  return (policy.defaultMode ?? 'suggest') !== 'off' || modes.some((mode) => mode !== 'off');
}

export const DEFAULT_AGENT_POLICY: AgentPolicy = {
  defaultMode: 'suggest',
  perChannel: {},
  autoSendThreshold: 0.8,
  blockedIntents: [...DEFAULT_BLOCKED_INTENTS],
};

/**
 * One human rung, which is the top of the business and cannot be removed.
 * `memberIds` is empty, which the UI reads as "the workspace owner", so
 * escalation reaches someone before anyone has configured a team.
 */
export const DEFAULT_ESCALATION_LADDER: EscalationLadder = {
  levels: [
    {
      id: 'level-1',
      name: 'Level 1',
      memberIds: [],
      channels: [],
      sla: { firstResponseMinutes: 60, resolutionMinutes: 24 * 60 },
      autoEscalateOnBreach: false,
      notify: { inApp: true, email: true, whatsapp: false },
    },
  ],
};

export type EscalationReasonCode =
  | 'low_confidence'
  | 'not_in_knowledge_base'
  | 'refund_or_payment'
  | 'complaint'
  | 'legal_or_safety'
  | 'customer_asked_for_human'
  | 'sla_breach'
  /**
   * The agent has replied its limit of times in this one conversation. A
   * thread still going round after six attempts is not being resolved, and
   * each further attempt costs money to make it no better.
   */
  | 'conversation_reply_cap'
  /**
   * The platform named a message without giving its text (an Instagram
   * mention the Graph API would not return). There is nothing to answer
   * from, so the agent drafts nothing and a person looks at it.
   */
  | 'message_unreadable'
  | 'other';

/** Reasons a person can pick. The agent also uses the first two on its own. */
export const ESCALATION_REASONS: readonly EscalationReasonCode[] = [
  'refund_or_payment',
  'complaint',
  'legal_or_safety',
  'customer_asked_for_human',
  'not_in_knowledge_base',
  'low_confidence',
  'other',
];

export interface ConversationEscalation {
  /** Level the conversation was moved to. */
  levelId: string;
  reason: EscalationReasonCode;
  note: string | null;
  /** User id, or `ai` when the agent escalated on its own. */
  by: string;
  at: string;
}

export type ConversationStatus =
  | 'ai_handling'
  | 'ai_resolved'
  | 'open'
  | 'waiting_on_customer'
  | 'snoozed'
  | 'resolved';

export type Sentiment = 'positive' | 'neutral' | 'negative';

/** Agent and escalation state for one conversation. */
export interface ConversationState {
  /** `${platform}:${conversationId}` for DMs, the event id for a comment or mention. */
  conversationKey: string;
  /** `ai` or an EscalationLevel id. */
  levelId: string;
  status: ConversationStatus;
  assigneeId: string | null;
  escalation: ConversationEscalation | null;
  snoozedUntil: string | null;
  sentiment: Sentiment | null;
  /** Agent's intent label, e.g. `opening_hours`. */
  intent: string | null;
  /** BCP 47 language of the customer's last message. */
  language: string | null;
  /** Reply ids the agent authored, so the UI can mark them as machine-written. */
  aiReplyIds: string[];
  /** Set for ten seconds after the agent sends on its own, to allow undo. */
  autoSent: { replyId: string; at: string } | null;
}

export interface AiDraftSource {
  title: string;
  /** Knowledge source id, when the snippet came from the knowledge base. */
  sourceId: string | null;
}

export interface AiDraft {
  text: string;
  /** 0–1, or null when the backend does not report one yet. */
  confidence: number | null;
  language: string | null;
  intent: string | null;
  sources: AiDraftSource[];
  /** True when this came from the mock adapter, so the UI can label it as a sample. */
  isSample: boolean;
}

/**
 * `feedback` is a person correcting the classified intent (retrains A02);
 * `edited` is a person changing the AI draft before sending (tone examples, A09).
 */
export type AiActivityKind = 'classified' | 'knowledge_hit' | 'answered' | 'suggested' | 'escalated' | 'resolved' | 'feedback' | 'edited';

export interface AiActivityEntry {
  id: string;
  kind: AiActivityKind;
  /** Short machine-readable detail: the intent, the source title, the reason code. */
  detail: string | null;
  confidence: number | null;
  at: string;
  /** For `feedback`: the intent the AI had picked. For `edited`: how many characters the person changed. */
  meta?: { intentBefore?: string; changedChars?: number };
}

/**
 * What a workspace knows about one customer. The key merges the same person
 * across Instagram, Facebook and Telegram by handle; a WhatsApp identity is
 * never merged with another channel (WhatsApp Business policy: no profile
 * built across channels from a WhatsApp user's data).
 */
export interface CustomerRecord {
  customerKey: string;
  notes: string;
  /** Blocked customers stay out of "Needs me" and never get an AI reply. */
  blocked: boolean;
  /**
   * The customer replied STOP (or a localised equivalent). The agent no
   * longer answers them, and no marketing template may be sent to them.
   */
  optedOut: boolean;
  /** When they opted out, ISO 8601; null when they have not. */
  optedOutAt: string | null;
  updatedAt: string;
}

export interface InboxStat {
  value: number;
  /** Change against the previous period, in the stat's own unit. Null when unknown. */
  delta: number | null;
}

/** The four numbers on the inbox stats strip. */
export interface InboxStats {
  /** 0–1. */
  resolvedByAi: InboxStat;
  medianFirstResponseMinutes: InboxStat;
  openAtHumanLevels: InboxStat;
  slaBreachesThisWeek: InboxStat;
}

export type KnowledgeSourceKind = 'text' | 'website' | 'file';

export interface KnowledgeSource {
  id: string;
  kind: KnowledgeSourceKind;
  title: string;
  /** BCP 47 language the content is written in. */
  language: string;
  /** The pasted text, the URL, or the file name. */
  content: string;
  status: 'ready' | 'processing' | 'failed';
  updatedAt: string;
}

/** Inbox views, in the order the filter pane lists them. */
export type InboxView =
  | 'all'
  | 'needs_me'
  | 'waiting_on_customer'
  | 'handled_by_ai'
  | 'assigned_to_me'
  | 'escalated_to_me'
  | 'sla_at_risk'
  | 'snoozed'
  | 'resolved';

export const INBOX_VIEWS: readonly InboxView[] = [
  'all',
  'needs_me',
  'waiting_on_customer',
  'handled_by_ai',
  'assigned_to_me',
  'escalated_to_me',
  'sla_at_risk',
  'snoozed',
  'resolved',
];

/**
 * A conversation's response deadline, from the server's SLA tracker (the
 * Settings → Inbox SLA rules): the same clock that raises the breach alert.
 */
export interface ConversationSla {
  /** When the first response is due (working minutes counted, ISO 8601). */
  dueAt: string;
  /** The tracker has breached (or the deadline has passed). */
  breached: boolean;
  /** The rule's response time, for the "at risk" margin. */
  windowMinutes: number;
}

/**
 * One row of the server's conversation list (GET /inbox/conversations):
 * the whole conversation summarised in SQL, whatever its age, so views,
 * counts and search describe every conversation, not the loaded ones.
 * `events` holds the latest message and the latest customer message (with
 * their replies), in the list's event shape, for the row's preview.
 */
export interface InboxConversationSummary<E = unknown> {
  key: string;
  /** SCREAMING_CASE platform. */
  channel: string;
  lastAt: string;
  lastInboundAt: string | null;
  unread: boolean;
  starred: boolean;
  awaitingReply: boolean;
  /** The state as the server derives it (stored, else where the policy places it). */
  state: ConversationState & { updatedAt?: string };
  sla: ConversationSla | null;
  blocked: boolean;
  events: E[];
}

export interface InboxConversationPage<E = unknown> {
  data: Array<InboxConversationSummary<E>>;
  /** Pass back as `cursor` for the next page; null at the end. */
  nextCursor: string | null;
  /** Conversations per view under the same filters (not just this page). */
  counts: Record<InboxView, number>;
  /** Unread messages of the brand, unfiltered (the nav badge). */
  unreadCount: number;
  /** Open conversations per assignee, over every conversation (the assign popover's load). */
  loads: Record<string, number>;
}

/* ── Keys shared by the browser and the server ─────────────────────────── */

/**
 * How a conversation is identified everywhere: `${platform}:${conversationId}`
 * for a DM thread, the event id for a comment or mention. The platform is
 * lower-cased so the browser and the server produce the same key for the
 * same row whichever casing they hold the platform in.
 */
export function conversationKeyFor(event: {
  id: string;
  platform: string;
  conversationId: string | null;
}): string {
  return event.conversationId ? `${event.platform.toLowerCase()}:${event.conversationId}` : event.id;
}

/** The platform a DM key carries, SCREAMING_CASE, or null for a comment or mention. */
export function channelFromConversationKey(key: string): string | null {
  const at = key.indexOf(':');
  return at > 0 ? key.slice(0, at).toUpperCase() : null;
}

/**
 * Which customer record a sender belongs to. Both sides must derive this
 * identically, or a customer blocked in the browser is not blocked from the
 * agent; give `platform` whenever the event's platform is known (every caller
 * does).
 *
 * - A phone number is one person on any channel: `phone:<digits>`.
 * - A handle or a bare name is only unique on its own platform: @rahul on
 *   Instagram and @rahul on X are different people, so the key carries the
 *   platform (`instagram:handle:rahul`, `x:name:rahul`). Without it they
 *   shared one record: notes, a block, an opt-out and marketing consent
 *   leaked from one person to another.
 * - WhatsApp is never merged with anything (`whatsapp:<digits>`).
 *
 * Without a platform the old, platform-less key comes back (`handle:…`,
 * `name:…`); records stored under it before the change are found through
 * `customerKeysFor`.
 */
export function customerKeyFor(input: { handle: string; peer: string; platform?: string | null }): string {
  const raw = input.handle.trim();
  // WhatsApp customers stay on their own key: their number is not joined to
  // any other channel's identity, so nothing about them is pooled across
  // platforms. Pass the platform wherever it is known.
  if (input.platform?.toUpperCase() === 'WHATSAPP') {
    const digits = raw.replace(/[^\d]/g, '');
    return digits ? `whatsapp:${digits}` : `whatsapp-name:${input.peer.trim().toLowerCase()}`;
  }
  const digits = raw.replace(/[^\d]/g, '');
  if (digits.length >= 7 && /^[+\d\s().-]+$/.test(raw)) return `phone:${digits}`;
  const scope = input.platform?.trim() ? `${input.platform.trim().toLowerCase()}:` : '';
  const handle = raw.replace(/^@+/, '').toLowerCase();
  if (handle) return `${scope}handle:${handle}`;
  return `${scope}name:${input.peer.trim().toLowerCase()}`;
}

/**
 * Every key a customer record of this sender can sit under: the current
 * `customerKeyFor` key and the platform-less key records were stored under
 * before (`phone:…` for WhatsApp, `handle:…`/`name:…` for the others; the
 * 20261102000400 migration copies those to the platform keys). Deletes use
 * this so no older record (notes, opt-out, consent) is left behind.
 */
export function customerKeysFor(input: { handle: string; peer: string; platform?: string | null }): string[] {
  const current = customerKeyFor(input);
  const legacy = customerKeyFor({ handle: input.handle, peer: input.peer });
  return legacy === current ? [current] : [current, legacy];
}
